import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Box, Grid, Typography, Stack, Skeleton } from "@mui/material";

// ** Hooks
import { useSession } from "@/hooks/useSession.tsx";
import { useGetDashboardStatsQuery } from "@/api/dashboardApi.ts";

// ** Components
import KpiCard from "@/views/admin/dashboard/KpiCard.tsx";
import RecentApplicationsTable from "@/views/admin/dashboard/RecentApplicationsTable.tsx";
import ActivityTimeline from "@/views/admin/dashboard/ActivityTimeline.tsx";

export const Route = createFileRoute("/admin/")({
  component: DashboardPage,
});

function DashboardPage() {
  const { t } = useTranslation();
  const session = useSession();
  const { data: stats, isLoading, error } = useGetDashboardStatsQuery();

  const firstName = session?.user?.fullName?.split(" ")[0] ?? "Admin";

  if (error) {
    return (
      <Box>
        <Typography color="error">
          Erreur lors du chargement du dashboard
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Stack mb={4}>
        <Typography variant="h4" fontWeight={600} color="text.primary">
          {t("admin.dashboard.welcome", { name: firstName })}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {t("admin.dashboard.subtitle")}
        </Typography>
      </Stack>

      {/* KPI Cards */}
      <Grid container spacing={3} mb={4}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          {isLoading ? (
            <Skeleton variant="rectangular" height={120} />
          ) : (
            <KpiCard
              title={t("admin.dashboard.kpi.availableStudios")}
              value={stats?.studiosAvailable ?? 0}
              icon="tabler:home-check"
            />
          )}
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          {isLoading ? (
            <Skeleton variant="rectangular" height={120} />
          ) : (
            <KpiCard
              title={t("admin.dashboard.kpi.occupiedStudios")}
              value={stats?.studiosOccupied ?? 0}
              icon="tabler:home-x"
            />
          )}
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          {isLoading ? (
            <Skeleton variant="rectangular" height={120} />
          ) : (
            <KpiCard
              title={t("admin.dashboard.kpi.newApplications")}
              value={stats?.newApplicationsThisMonth ?? 0}
              icon="tabler:file-plus"
              subtitle={t("admin.dashboard.thisMonth")}
            />
          )}
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          {isLoading ? (
            <Skeleton variant="rectangular" height={120} />
          ) : (
            <KpiCard
              title={t("admin.dashboard.kpi.occupancyRate")}
              value={`${stats?.occupancyRate ?? 0}%`}
              icon="tabler:chart-pie"
            />
          )}
        </Grid>
      </Grid>

      {/* Content Grid */}
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, lg: 8 }}>
          <RecentApplicationsTable
            applications={stats?.recentApplications ?? []}
            isLoading={isLoading}
          />
        </Grid>
        <Grid size={{ xs: 12, lg: 4 }}>
          <ActivityTimeline
            activities={stats?.recentActivity ?? []}
            isLoading={isLoading}
          />
        </Grid>
      </Grid>
    </Box>
  );
}
